import { useCallback, useEffect, useState } from "react";
import { Icon } from "../components/Icon";
import { LoadingState } from "../components/LoadingState";
import { supabase } from "../lib/supabase";
import type { DashboardEventType, HostProfile } from "../lib/types";

const DEMO_HOSTS: HostProfile[] = [
  { id: 1, organization_id: 1, user_id: null, slug: "shahil", display_name: "Shahil Mathapershad", domain_label: "Capital & Credit", role: "Quantitative Analyst, ABSA Group", bio: "", timezone: "Africa/Johannesburg", is_active: true },
  { id: 2, organization_id: 1, user_id: null, slug: "rashik", display_name: "Rashik Singh", domain_label: "Operations & Supply", role: "Industrial Pharmacist, Medipost Holdings", bio: "", timezone: "Africa/Johannesburg", is_active: true },
];

const DEMO_EVENTS: DashboardEventType[] = DEMO_HOSTS.map((host) => ({
  id: host.id,
  organization_id: 1,
  host_profile_id: host.id,
  slug: `${host.slug}-30`,
  title: "30-minute working session",
  description: host.slug === "shahil" ? "Bring the problem. Leave with a clearer next move." : "A focused conversation about the operational problem in front of you.",
  duration_minutes: 30,
  slot_interval_minutes: 30,
  buffer_before_minutes: 10,
  buffer_after_minutes: 10,
  minimum_notice_minutes: 120,
  booking_horizon_days: 60,
  location_label: "Online meeting",
  meeting_url: null,
  is_active: true,
}));

export function EventTypesPage() {
  const [hosts, setHosts] = useState<HostProfile[]>([]);
  const [events, setEvents] = useState<DashboardEventType[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!supabase) {
      setHosts(DEMO_HOSTS);
      setEvents(DEMO_EVENTS);
      setLoading(false);
      return;
    }
    const [hostResult, eventResult] = await Promise.all([
      supabase.from("host_profiles").select("id, organization_id, user_id, slug, display_name, domain_label, role, bio, timezone, is_active").order("id"),
      supabase.from("event_types").select("id, organization_id, host_profile_id, slug, title, description, duration_minutes, slot_interval_minutes, buffer_before_minutes, buffer_after_minutes, minimum_notice_minutes, booking_horizon_days, location_label, meeting_url, is_active").order("id"),
    ]);
    if (hostResult.error || eventResult.error) setError(hostResult.error?.message ?? eventResult.error?.message ?? "Meeting types could not be loaded.");
    else {
      setHosts((hostResult.data ?? []) as HostProfile[]);
      setEvents((eventResult.data ?? []) as DashboardEventType[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const update = (id: number, patch: Partial<DashboardEventType>) => {
    setEvents((current) => current.map((event) => event.id === id ? { ...event, ...patch } : event));
    setMessage(null);
  };

  const save = async (eventType: DashboardEventType) => {
    setSavingId(eventType.id);
    setError(null);
    setMessage(null);
    if (supabase) {
      const changes = {
        title: eventType.title,
        description: eventType.description,
        duration_minutes: eventType.duration_minutes,
        slot_interval_minutes: eventType.slot_interval_minutes,
        buffer_before_minutes: eventType.buffer_before_minutes,
        buffer_after_minutes: eventType.buffer_after_minutes,
        minimum_notice_minutes: eventType.minimum_notice_minutes,
        booking_horizon_days: eventType.booking_horizon_days,
        location_label: eventType.location_label,
        meeting_url: eventType.meeting_url || null,
        is_active: eventType.is_active,
      };
      const { error: saveError } = await supabase.from("event_types").update(changes).eq("id", eventType.id);
      if (saveError) setError(saveError.message);
      else setMessage(`${eventType.title} is updated.`);
    } else setMessage(`${eventType.title} is updated in preview mode.`);
    setSavingId(null);
  };

  const copyLink = async (slug: string) => {
    const link = `${window.location.origin}/book/${slug}`;
    try {
      await navigator.clipboard.writeText(link);
      setMessage("Public booking link copied.");
    } catch {
      setMessage(link);
    }
  };

  return (
    <main className="settings-page">
      <header className="settings-hero">
        <div><p className="section-label">Public booking pages</p><h1>Meeting types</h1></div>
        <p>Shape the invitation, booking limits, buffers, and meeting destination for each host.</p>
      </header>
      {error ? <div className="settings-message settings-message--error" role="alert">{error}</div> : null}
      {message ? <div className="settings-message" role="status"><Icon name="check" /> {message}</div> : null}
      {loading ? <LoadingState label="Loading meeting types" /> : (
        <div className="event-type-grid">
          {events.map((eventType) => {
            const host = hosts.find((item) => item.id === eventType.host_profile_id);
            return (
              <form className="event-type-card" key={eventType.id} onSubmit={(formEvent) => { formEvent.preventDefault(); void save(eventType); }}>
                <header>
                  <div><p>{host?.domain_label ?? "RSM"}</p><h2>{host?.display_name ?? "RSM host"}</h2></div>
                  <label className="status-toggle"><input type="checkbox" checked={eventType.is_active} onChange={(input) => update(eventType.id, { is_active: input.target.checked })} /><span>{eventType.is_active ? "Live" : "Hidden"}</span></label>
                </header>
                <div className="event-type-link"><code>/book/{eventType.slug}</code><button type="button" onClick={() => void copyLink(eventType.slug)}>Copy link</button></div>
                <label><span>Meeting title</span><input required maxLength={120} value={eventType.title} onChange={(input) => update(eventType.id, { title: input.target.value })} /></label>
                <label><span>Invitation copy</span><textarea rows={3} maxLength={600} value={eventType.description} onChange={(input) => update(eventType.id, { description: input.target.value })} /></label>
                <div className="event-number-grid">
                  <label><span>Duration</span><div><input type="number" min={15} max={240} step={15} value={eventType.duration_minutes} onChange={(input) => update(eventType.id, { duration_minutes: Number(input.target.value) })} /><small>min</small></div></label>
                  <label><span>Start interval</span><div><input type="number" min={5} max={120} step={5} value={eventType.slot_interval_minutes} onChange={(input) => update(eventType.id, { slot_interval_minutes: Number(input.target.value) })} /><small>min</small></div></label>
                  <label><span>Minimum notice</span><div><input type="number" min={0} max={43200} step={30} value={eventType.minimum_notice_minutes} onChange={(input) => update(eventType.id, { minimum_notice_minutes: Number(input.target.value) })} /><small>min</small></div></label>
                  <label><span>Booking horizon</span><div><input type="number" min={1} max={365} value={eventType.booking_horizon_days} onChange={(input) => update(eventType.id, { booking_horizon_days: Number(input.target.value) })} /><small>days</small></div></label>
                  <label><span>Buffer before</span><div><input type="number" min={0} max={120} step={5} value={eventType.buffer_before_minutes} onChange={(input) => update(eventType.id, { buffer_before_minutes: Number(input.target.value) })} /><small>min</small></div></label>
                  <label><span>Buffer after</span><div><input type="number" min={0} max={120} step={5} value={eventType.buffer_after_minutes} onChange={(input) => update(eventType.id, { buffer_after_minutes: Number(input.target.value) })} /><small>min</small></div></label>
                </div>
                <label><span>Format label</span><input required maxLength={120} value={eventType.location_label} onChange={(input) => update(eventType.id, { location_label: input.target.value })} /></label>
                <label><span>Meeting URL <small>optional</small></span><input type="url" placeholder="https://meet.example.com/room" value={eventType.meeting_url ?? ""} onChange={(input) => update(eventType.id, { meeting_url: input.target.value || null })} /></label>
                <footer><p>Buffers protect the host&rsquo;s time without appearing on the guest&rsquo;s invitation.</p><button className="button button--primary" type="submit" disabled={savingId === eventType.id}>{savingId === eventType.id ? "Saving…" : "Save meeting type"}</button></footer>
              </form>
            );
          })}
        </div>
      )}
    </main>
  );
}
